(ns manage.adset-decision-tree-page.bulk-apply-trackers-ocp.hook
  (:require [cljs-bean.core :refer [->clj]]
            [clojure.data :as data]
            [clojure.string :as str]
            [common.validators.core :as validators]
            [manage.adset-decision-tree-page.editor.util :refer [build-decision-path]]
            [manage.adset-decision-tree-page.sub :as dt-sub]
            [mate.core :as mc]
            [uix.core :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(defn- variant->row-data [node-id->node-data node-id variant]
  (let [variant-trackers (get variant :trackers [])
        click-tracker (->> variant-trackers
                           (filter #(= (:type %) "click"))
                           first
                           :value)
        impression-trackers (->> variant-trackers
                                 (filter #(= (:type %) "impression"))
                                 (mapv :value))]
    {:node-id node-id
     :decision-path (build-decision-path node-id->node-data node-id)
     :content-id (:content-id variant)
     :ad-variant-uuid (:ad-variant-uuid variant)
     :uuid (:uuid variant)
     :click-tracker click-tracker
     :impression-trackers impression-trackers}))

(defn get-trackers-for-rows [variants]
  (into {:clickTrackers
         (let [click-trackers (map :click-tracker variants)]
           (if (apply = click-trackers)
             (first click-trackers)
             (mc/index-by (comp keyword :uuid) :click-tracker
                          (filter :click-tracker variants))))}
        (->> (reduce
              (fn [acc {:keys [impression-trackers uuid]}]
                (reduce (fn [acc' [i tracker]]
                          (update acc' i conj [(keyword uuid) tracker]))
                        acc
                        (mc/seq-indexed impression-trackers)))
              []
              variants)
             (map-indexed (fn [i trackers]
                            [(keyword (str "impressionTrackers-" i))
                             (if (or (not (apply = (map second trackers)))
                                     (not= (count variants) (count trackers)))
                               (into {} trackers)
                               (second (first trackers)))])))))

(defn- group-variants-by-content [node-id->node-data]
  (->> node-id->node-data
       (mapcat (fn [[node-id node]]
                 (map #(variant->row-data node-id->node-data node-id %)
                      (:ad-variants node))))
       (group-by (juxt :node-id :content-id))
       (mapv (fn [[[node-id content-id] variants]]
               (let [variant-count (count variants)
                     trackers-for-rows (get-trackers-for-rows variants)]
                 (merge {:id (str node-id "-" content-id)
                         :node-id node-id
                         :variants variants
                         :impression-trackers (vals (dissoc trackers-for-rows :clickTrackers))
                         :adVariant (if (> variant-count 1)
                                      (str content-id " (" variant-count ")")
                                      content-id)
                         :decisionPath (:decision-path (first variants))}
                        trackers-for-rows))))))

(defn use-initial-rows []
  (let [node-id->node-data (use-subscribe [::dt-sub/node-id->node-data])]
    (uix/use-memo #(group-variants-by-content node-id->node-data) [node-id->node-data])))

(defn use-columns-controls [{:keys [remove-column-data initial-rows]}]
  (let [[impression-columns set-impression-columns]
        (uix/use-state (->> (map (comp count :impression-trackers) initial-rows)
                            (apply max)
                            (range)
                            (mapv (fn [i] (str "impressionTrackers-" i)))))
        [has-click-column set-has-click-column] (uix/use-state
                                                 (some :clickTrackers initial-rows))
        add-impression-column (uix/use-callback
                               #(set-impression-columns
                                 (fn [cols]
                                   (conj cols (str "impressionTrackers-" (random-uuid)))))
                               [])
        delete-column (uix/use-callback
                       (fn [field]
                         (if (= field "clickTrackers")
                           (set-has-click-column false)
                           (set-impression-columns
                            #(vec (remove #{field} %))))
                         (remove-column-data field))
                       [remove-column-data])]
    (uix/use-memo (fn [] {:impression-columns impression-columns
                          :has-click-column has-click-column
                          :set-has-click-column set-has-click-column
                          :add-impression-column add-impression-column
                          :delete-column delete-column})
                  [impression-columns has-click-column add-impression-column delete-column])))

(defn use-filtered-editable-rows [initial-rows]
  (let [[rows set-rows] (uix/use-state (clj->js initial-rows))
        process-row-update (uix/use-callback
                            (fn [^js new-row ^js _old-row]
                              (set-rows (fn [^js current-rows]
                                          (.map current-rows
                                                (fn [^js row]
                                                  (if (= (.-id row) (.-id new-row))
                                                    new-row row)))))
                              new-row)
                            [])
        remove-column-data (uix/use-callback
                            (fn [field]
                              (set-rows (fn [^js current-rows]
                                          (.map current-rows
                                                (fn [^js row]
                                                  (js-delete row field)
                                                  row)))))
                            [])]
    {:rows rows
     :process-row-update process-row-update
     :remove-column-data remove-column-data}))

(defn get-save-data [rows impression-columns initial-rows]
  (let [id->initial-row (mc/index-by :id initial-rows)
        changed-rows
        (map (fn [row]
               (let [impression-trackers (seq (keep (fn [col] (get row (keyword col)))
                                                    impression-columns))
                     {:keys [valid invalid]}
                     (->> (map (fn [tracker] {:type "impression" :value tracker})
                               impression-trackers)
                          (into [{:type "click" :value (:clickTrackers row)}])
                          (group-by (fn [{:keys [value]}]
                                      (if (or (map? value)
                                              (str/blank? value)
                                              (validators/url? value))
                                        :valid
                                        :invalid))))]
                 (assoc row
                        :trackers valid
                        :impression-trackers impression-trackers
                        :has-invalid (seq invalid))))
             (->clj rows))

        get-rows-for-diff
        #(->> %
              (map (fn [row] (select-keys row [:impression-trackers :clickTrackers :id])))
              (mc/index-by :id))

        [before after] (data/diff (-> initial-rows
                                      get-rows-for-diff
                                      (select-keys (map :id changed-rows)))
                                  (get-rows-for-diff changed-rows))

        changed-node-ids (->> (concat (keys before) (keys after))
                              (map (comp :node-id id->initial-row))
                              set)]
    {:valid (not-any? :has-invalid changed-rows)
     :bulk-patch-data
     (->> (mapcat (fn [{:keys [trackers node-id variants]}]
                    (when (seq trackers)
                      (mapv (fn [{ad-uuid :uuid}]
                              {:node-id node-id
                               :uuid ad-uuid
                               :trackers
                               (keep
                                (fn [{:keys [value] :as tracker}]
                                  (if (map? value)
                                    (when-let [per-ad-value (get value (keyword ad-uuid))]
                                      (assoc tracker :value per-ad-value))
                                    (when-not (str/blank? value) tracker)))
                                trackers)})
                            variants)))
                  changed-rows)
          (group-by :node-id)
          (keep (fn [[node-id variants]]
                  (when (contains? changed-node-ids node-id)
                    {:id node-id
                     :ad-variants (mapv (fn [{:keys [uuid trackers]}]
                                          {:uuid uuid :trackers trackers})
                                        variants)}))))
     :has-overwrites
     (some (fn [[row-id {click-changed :clickTrackers
                         impressions-changed :impression-trackers}]]
             (let [{:keys [clickTrackers impression-trackers]} (get id->initial-row row-id)]
               (cond
                 click-changed (some? clickTrackers)
                 impressions-changed
                 (->> impressions-changed
                      (map-indexed
                       (fn [i impression-changed]
                         (when impression-changed
                           (some? (get (vec impression-trackers) i)))))
                      (some boolean)))))
           after)}))
